/**
 * Journeys (Build 3 J3): shapes of the graph and of the API answers, labels and the pure helpers shared by the
 * customer dashboard and «Маяк» (apps/admin imports this folder through the `@web/journeys` alias). No API client
 * here: each app fetches with its own one.
 */

import { currentLocale, t } from './i18nBridge';

// ---------- the graph (docs/journeys/SPEC.md §3.2) ----------

export type Scalar = string | number | boolean | null;
export type Duration = { minutes?: number; hours?: number; days?: number };
export type PropFilter = { property: string; op: string; value?: unknown };
export type Condition = Record<string, unknown>;
export type VariableSource = Scalar | { path: string; default?: Scalar };

export type Trigger =
  | { type: 'event'; event: string; where?: PropFilter[] }
  | { type: 'segment_entry'; segment: string }
  | { type: 'date'; attribute: string; offset_days: number; at_local: string; recurrence: 'once' | 'yearly' }
  | { type: 'api' };

export interface Goal {
  event: string;
  where?: PropFilter[];
  window_days: number;
  exit_on_goal: boolean;
  value_property?: string;
}

export interface Delivery {
  stream: 'marketing' | 'transactional';
  from?: { email: string; name?: string };
  reply_to?: string;
  quiet_hours?: { start: string; end: string };
  smart_sending_hours?: number;
  max_lateness_hours?: number | null;
  frequency_cap?: { max: number; per_days: number };
}

export type Step =
  | { type: 'wait'; duration?: Duration; until_local?: { time: string; weekdays?: number[] }; next: string | null }
  | { type: 'wait_for_event'; event: string; where?: PropFilter[]; timeout: Duration; on_event: string | null; on_timeout: string | null }
  | { type: 'branch'; conditions: { when: Condition; next: string | null }[]; else: string | null }
  | { type: 'split'; variants: { id: string; weight: number; next: string | null }[] }
  | {
      type: 'send_email';
      template: { id?: string; alias?: string; version?: number | 'latest' };
      variables: Record<string, VariableSource>;
      stream?: 'marketing' | 'transactional';
      from?: { email: string; name?: string };
      reply_to?: string;
      smart_sending?: boolean;
      on_skip?: 'continue' | 'exit';
      next: string | null;
    }
  | {
      /** Build 5: a notification through the channels of a cascade policy; versions are pinned at save (docs/channels/BUILD-SPEC.md §4.10). */
      type: 'send_notification';
      template: { key: string; version?: number | 'latest' };
      policy: { key: string; version?: number | 'latest' };
      variables: Record<string, VariableSource>;
      stream?: 'marketing' | 'transactional';
      on_skip?: 'continue' | 'exit';
      next: string | null;
    }
  | { type: 'webhook'; endpoint_id: string; include_attributes?: string[]; next: string | null }
  | { type: 'update_contact'; set: Record<string, Scalar | { path: string }>; unset?: string[]; next: string | null }
  | { type: 'digest'; event: string; where?: PropFilter[]; window: { minutes?: number; hours?: number }; max_items: number; item_template: string; next: string | null }
  | { type: 'exit'; reason: string };

export type StepType = Step['type'];

export interface Graph {
  schema: 'mailocean.journey/v1';
  name?: string;
  trigger: Trigger;
  entry_filter?: Condition;
  exit_filter?: Condition;
  reentry?: { mode: 'never' | 'after_exit' | 'after_cooldown'; cooldown_hours?: number };
  goal?: Goal;
  holdout?: { percent: number };
  delivery: Delivery;
  start: string;
  steps: Record<string, Step>;
  layout?: Record<string, { x: number; y: number }>;
}

// ---------- API answers ----------

export type JourneyStatus = 'draft' | 'active' | 'paused' | 'stopped';

export interface Journey {
  id: string;
  key: string;
  name: string;
  status: JourneyStatus;
  active_version: number | null;
  latest_version: number;
  trigger: { type: string; ref: string | null } | null;
  library_key: string | null;
  locked_by_staff: boolean;
  staff_message?: string;
  paused_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JourneyListItem extends Journey {
  enrollments: { active: number; control: number };
  emails_7d?: { accepted: number };
  measurement?: {
    goal: { event: string; window_days: number } | null;
    incremental: { available: boolean; lift_abs?: number | null; ci95_abs?: [number, number] | null; significant?: boolean | null; min_sample_ok?: boolean };
  } | null;
}

export interface VersionInfo {
  version: number;
  hash: string;
  graph: Graph;
  /** Editor positions (journey_layouts, else graph.layout, else null = automatic). Not part of the hash. */
  layout?: Record<string, { x: number; y: number }> | null;
  source: string;
  created_at: string;
  published_at: string | null;
}

/** The shared autosaved editor draft (docs/pilot/SPEC.md §12.3). */
export interface EditorDraft {
  journey_id: string;
  base_version: number;
  graph: Graph;
  layout: Record<string, { x: number; y: number }>;
  rev: number;
  updated_by: { id: string; name: string | null };
  updated_at: string;
}

export interface JourneyDetail extends Journey {
  active: VersionInfo | null;
  latest: VersionInfo | null;
  approval: { status: string };
  /** The dashboard tells whether an autosaved editor draft exists (docs/pilot/SPEC.md §12.3). */
  editor_draft?: { rev: number; base_version: number; updated_at: string } | null;
}

export interface VersionRow {
  version: number;
  hash: string;
  source: string;
  created_by: string | null;
  created_at: string;
  published_at: string | null;
  approval: { id: string; status: string } | null;
}

export interface StepStats {
  type: string;
  now: number;
  entered: number;
  outcomes: Record<string, number>;
  held: number;
  errors: number;
  emails?: Record<string, number>;
  /** Build 5: notification steps — notifications by status, channel steps by honest signal, cost. */
  notifications?: { created: number; by_status: Record<string, number>; cost: string; channels: Record<string, { accepted: number; delivered: number; read: number; skipped: number; failed: number }> };
}

export interface JourneyStats {
  version: number;
  computed_at: string;
  enrollments: Record<string, number>;
  exits: Record<string, number>;
  skipped_entries: Record<string, number>;
  steps: Record<string, StepStats>;
  labels: Record<string, string>;
}

export interface ApprovalSummary {
  version: number;
  hash: string;
  trigger: string;
  stream: string;
  from: string | null;
  audience_estimate: { triggers_last_7d: number | null; segment_count: number | null; eligible_share: number | null; excluded: { no_consent: number; suppressed: number } | null };
  holdout_percent: number;
  emails: { step: string; template: string; template_version: number; subject: string | null; stream: string; from: string }[];
  /** Build 5: notification steps with pinned policy and channel template versions. */
  notifications?: { step: string; policy: string; policy_version: number; template: string; template_version: number; stream: string }[];
  frequency: { smart_sending_hours: number; cap: string };
  quiet_hours: string | null;
  goal: string | null;
  max_lateness_hours: number | null;
  warnings: string[];
  transactional_notice?: string;
  diff_from_active: { added: string[]; removed: string[]; changed: string[] } | null;
  honest_metrics?: string;
}

export interface Approval {
  id: string;
  journey_id: string;
  version: number;
  hash: string;
  status: 'pending' | 'approved' | 'rejected' | 'void' | 'revoked';
  requested_by: string;
  requested_at: string;
  summary: ApprovalSummary;
  decided_by: string | null;
  decided_at: string | null;
  self_approved: boolean;
  comment: string | null;
}

export interface GraphDiff {
  from: number;
  to: number;
  added: string[];
  removed: string[];
  changed: { step?: string; path: string; from: unknown; to: unknown }[];
  top_level: { path: string; from: unknown; to: unknown }[];
}

export interface Enrollment {
  id: string;
  journey_id: string;
  version: number;
  contact_id: string;
  email?: string | null;
  status: 'active' | 'completed' | 'exited' | 'control' | 'control_done';
  group: 'treatment' | 'control';
  current_step: string | null;
  entry_key: string;
  assignments: Record<string, string>;
  entered_at: string;
  finished_at: string | null;
  exit_reason: string | null;
  goal_reached_at: string | null;
  goal_value: number | null;
  first_send_at: string | null;
  journey?: { key: string; name: string };
}

export interface RunLogEntry {
  id: number;
  at: string;
  version?: number;
  step_id: string | null;
  step_type?: string | null;
  kind: string;
  outcome: string;
  detail: Record<string, unknown> | null;
  message_id: string | null;
  contact_id?: string;
  enrollment_id?: string;
  email?: string;
}

export interface Report {
  version: number;
  window_days: number;
  period: { from: string | null; to: string };
  mature_only: boolean;
  incremental:
    | { available: false; text: string }
    | {
        available: true;
        label: string;
        treatment: { entered: number; goal: number; rate: number };
        control: { entered: number; goal: number; rate: number };
        lift_abs: number;
        lift_rel: number | null;
        ci95_abs: [number, number];
        significant: boolean | null;
        min_sample_ok: boolean;
        note?: string;
        value: { treatment_per_contact: number; control_per_contact: number; currency: string | null };
      };
  attributed: { label: string; goal_after_email: number; value_sum: number };
  variants: Record<string, { id: string; entered: number; goal: number; rate: number; ci95: [number, number] }[]>;
  caveats: string[];
}

export interface TraceEntry {
  at: string;
  step: string | null;
  type: string | null;
  outcome: string;
  detail?: Record<string, unknown>;
  email?: { subject: string | null; from: string; template: string; version: number; missing_variables: string[] };
  /** Build 5: the channel plan of a notification step (an estimate; nothing written). */
  notification?: { policy: { key: string; version: number }; template: { key: string; version: number }; steps: { seq: number; channel: string; state: string; skip_reason?: string; estimated_cost: string | null }[]; estimated_cost_max?: { amount: string }; error?: { code: string; message: string } };
}

export interface SimulationResult {
  version: number;
  start_at: string;
  horizon_days: number;
  note: string;
  contacts: { contact: { id: string; email: string | null; external_id: string | null }; status: string; exit_reason?: string; trace: TraceEntry[]; warnings: string[] }[];
}

export interface LibraryItem {
  key: string;
  name: string;
  description: string;
  trigger: Trigger;
  emails: { alias: string; name: { ru: string; en: string } | string; subject: { ru: string; en: string } | string }[];
  segments: { key: string; name: { ru: string; en: string } | string }[];
  note: string;
}

export interface ValidationIssue {
  path: string;
  message: string;
  hint?: string;
  did_you_mean?: string;
  code?: string;
}

// ---------- labels ----------

export const JOURNEY_STATUS: Record<JourneyStatus, { label: string; tone: string; hint: string }> = {
  active: { label: 'Течёт', tone: 'ok', hint: 'Сценарий активен: новые участники входят, шаги выполняются' },
  paused: { label: 'Штиль', tone: 'warn', hint: 'Пауза: шаги ждут, пока сценарий не возобновят' },
  stopped: { label: 'Остановлено', tone: 'cancelled', hint: 'Остановлен навсегда: участия завершены' },
  draft: { label: 'Черновик', tone: 'queued', hint: 'Нет опубликованной версии — ничего не отправляется' },
};

export const STEP_LABEL: Record<StepType, string> = {
  wait: 'Пауза',
  wait_for_event: 'Ждать событие',
  branch: 'Ветка',
  split: 'Сплит',
  send_email: 'Письмо',
  send_notification: 'Уведомление',
  webhook: 'Вебхук',
  update_contact: 'Обновить контакт',
  digest: 'Дайджест',
  exit: 'Выход',
};

export const STEP_HINT: Record<StepType, string> = {
  wait: 'Подождать срок или до местного времени контакта',
  wait_for_event: 'Ждать событие контакта с тайм-аутом',
  branch: 'Пойти по первому истинному условию',
  split: 'Разделить участников по вариантам (A/B)',
  send_email: 'Отправить письмо по закреплённой версии шаблона',
  send_notification: 'Каскад по каналам: push, Telegram, SMS, Web Push, письмо — по политике и шаблону канала',
  webhook: 'Поставить вебхук journey.webhook вашему серверу',
  update_contact: 'Записать или удалить атрибуты контакта',
  digest: 'Собрать события за окно в одно письмо',
  exit: 'Завершить участие',
};

export const TRIGGER_LABEL: Record<Trigger['type'], string> = {
  event: 'Событие',
  segment_entry: 'Вход в сегмент',
  date: 'Дата из атрибута',
  api: 'Зачисление через API',
};

export const OUTCOME_LABEL: Record<string, string> = {
  entered: 'вошёл',
  control_assigned: 'контрольная группа',
  scheduled: 'ожидание поставлено',
  waited: 'ожидание закончилось',
  event_received: 'событие пришло',
  timeout: 'тайм-аут',
  sent_queued: 'письмо поставлено в очередь',
  skipped_no_email: 'пропуск: нет адреса',
  skipped_no_consent: 'пропуск: нет согласия',
  skipped_suppressed: 'пропуск: адрес в подавлениях',
  skipped_smart_sending: 'пропуск: умная отправка',
  skipped_frequency_cap: 'пропуск: частотный лимит',
  skipped_stale: 'пропуск: опоздание',
  skipped_no_channel: 'пропуск: ни один канал не подошёл',
  failed_render: 'ошибка шаблона',
  webhook_queued: 'вебхук поставлен',
  contact_updated: 'контакт обновлён',
  digest_closed: 'дайджест собран',
  goal: 'цель достигнута',
  exit_filter: 'вышел по фильтру выхода',
  exit_step: 'шаг «Выход»',
  completed: 'дошёл до конца',
  control_done: 'контроль завершён',
  journey_stopped: 'сценарий остановлен',
  erased: 'контакт удалён (GDPR)',
  held: 'удержан',
  error: 'ошибка',
  migrated: 'перенесён в новую версию',
  skipped_entry: 'не вошёл',
  horizon: 'горизонт симуляции',
  not_eligible: 'не подходит',
};

export const HOLD_REASON: Record<string, string> = {
  journey_paused: 'сценарий на паузе',
  tenant_paused: 'отправка пространства приостановлена',
  tenant_suspended: 'отправка пространства заблокирована',
  stream_paused: 'поток приостановлен',
  kill_switch: 'глобальный стоп',
  quiet_hours: 'тихие часы',
  global_pause: 'пауза сценариев платформы',
};

export const EXIT_REASON: Record<string, string> = {
  goal: 'цель',
  exit_filter: 'фильтр выхода',
  exit_step: 'шаг «Выход»',
  journey_stopped: 'сценарий остановлен',
  erased: 'контакт удалён',
  migrated_out: 'перенесён',
  no_consent: 'нет согласия',
  suppressed: 'подавление',
  error: 'ошибка',
};

export const ENROLLMENT_STATUS: Record<Enrollment['status'], { label: string; tone: string }> = {
  active: { label: 'в пути', tone: 'sent' },
  completed: { label: 'дошёл до конца', tone: 'ok' },
  exited: { label: 'вышел', tone: 'cancelled' },
  control: { label: 'контрольная группа', tone: 'queued' },
  control_done: { label: 'контроль завершён', tone: 'cancelled' },
};

export const APPROVAL_STATUS: Record<string, { label: string; tone: string }> = {
  none: { label: 'не запрошено', tone: 'cancelled' },
  pending: { label: 'ждёт решения', tone: 'warn' },
  approved: { label: 'согласовано', tone: 'ok' },
  rejected: { label: 'возвращено', tone: 'bad' },
  void: { label: 'аннулировано', tone: 'cancelled' },
  revoked: { label: 'отозвано', tone: 'revoked' },
};

export const SOURCE_LABEL: Record<string, string> = { dashboard: 'кабинет', api: 'API', library: 'шаблон' };

/** Email statuses of a step, honest wording: «принято провайдером» is not «доставлено». */
export const EMAIL_STAT_LABEL: [string, string][] = [
  ['sent', 'принято'],
  ['delivered', 'доставлено'],
  ['bounced', 'отказы'],
  ['complained', 'жалобы'],
  ['queued', 'в очереди'],
  ['failed', 'не отправлено'],
  ['delivery_unknown', 'исход неизвестен'],
  ['cancelled', 'отменено'],
];

/** Accepted by the provider = every status after the hand-over that the provider confirmed. */
export const acceptedOf = (e: Record<string, number> = {}) => (e.sent ?? 0) + (e.delivered ?? 0) + (e.bounced ?? 0) + (e.complained ?? 0);

export function outcomeLabel(o: string): string {
  if (o.startsWith('branch:')) return t('ветка → {x}', { x: o.slice(7) });
  if (o.startsWith('variant:')) return t('вариант {x}', { x: o.slice(8) });
  return t(OUTCOME_LABEL[o] ?? o);
}

export function outcomeTone(o: string): 'good' | 'bad' | 'warn' | '' {
  if (o === 'goal' || o === 'completed' || o === 'sent_queued' || o === 'event_received') return 'good';
  if (o === 'error' || o === 'failed_render' || o === 'skipped_suppressed' || o === 'erased') return 'bad';
  if (o.startsWith('skipped') || o === 'held' || o === 'timeout' || o === 'journey_stopped' || o === 'exit_filter') return 'warn';
  return '';
}

// ---------- words ----------

const plural = (n: number, one: string, few: string, many: string) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
};

export function durationText(d: Duration | undefined): string {
  if (!d) return '—';
  const parts = [
    d.days ? t('{n} {w}', { n: d.days, w: t(plural(d.days, 'день', 'дня', 'дней')) }) : '',
    d.hours ? t('{n} ч', { n: d.hours }) : '',
    d.minutes ? t('{n} мин', { n: d.minutes }) : '',
  ].filter(Boolean);
  return parts.join(' ') || t('0 мин');
}

const WEEKDAYS = ['', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const OP_WORD: Record<string, string> = {
  eq: '=', neq: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', in: 'одно из', not_in: 'ни одно из', contains: 'содержит', starts_with: 'начинается с',
  exists: 'заполнено', not_exists: 'не заполнено', between: 'между', within_last_days: 'за последние', more_than_days_ago: 'больше … дней назад', within_next_days: 'в ближайшие',
  is_true: 'да', is_false: 'нет',
};

const valueText = (v: unknown): string => {
  if (v && typeof v === 'object' && 'ref' in (v as object)) return `= ${(v as { ref: string }).ref}`;
  if (Array.isArray(v)) return v.map(valueText).join(', ');
  return typeof v === 'string' ? `«${v}»` : JSON.stringify(v);
};

export function filtersText(where?: PropFilter[]): string {
  if (!where?.length) return '';
  return where.map((w) => `${w.property} ${t(OP_WORD[w.op] ?? w.op)}${w.value === undefined ? '' : ` ${valueText(w.value)}`}`).join(` ${t('и')} `);
}

/** A condition tree in words (short; the panel shows the full form). */
export function conditionText(c: unknown, depth = 0): string {
  if (!c || typeof c !== 'object') return String(c);
  const o = c as Record<string, unknown>;
  if (Array.isArray(o.all)) return o.all.map((x) => conditionText(x, depth + 1)).join(` ${t('и')} `) || t('всегда');
  if (Array.isArray(o.any)) return `(${o.any.map((x) => conditionText(x, depth + 1)).join(` ${t('или')} `)})`;
  if (o.not) return `${t('не')} ${conditionText(o.not, depth + 1)}`;
  if (typeof o.attribute === 'string') return `${o.attribute} ${t(OP_WORD[String(o.op)] ?? String(o.op))}${o.value === undefined ? '' : ` ${valueText(o.value)}`}`;
  if (typeof o.field === 'string') return `${o.field} ${t(OP_WORD[String(o.op)] ?? String(o.op))}${o.value === undefined ? '' : ` ${valueText(o.value)}`}`;
  if (o.consent) return o.status === 'granted' ? t('есть согласие') : o.status === 'withdrawn' ? t('согласие отозвано') : t('нет записи о согласии');
  if (typeof o.segment === 'string') return t('в сегменте {s}', { s: o.segment });
  if (typeof o.event === 'string') {
    const w = o.within as Duration | undefined;
    const cnt = o.count as { op: string; value: number } | undefined;
    return t('было {event}{count}{within}{where}', { event: String(o.event), count: cnt ? ` ${t(OP_WORD[cnt.op] ?? cnt.op)} ${cnt.value} ${t('раз')}` : '', within: w ? ` ${t('за {d}', { d: durationText(w) })}` : '', where: Array.isArray(o.where) && o.where.length ? ` (${filtersText(o.where as PropFilter[])})` : '' });
  }
  if (typeof o.step_message === 'string') return t('письмо шага {m}: {s}', { m: o.step_message, s: String(o.status) });
  if (typeof o.waited_event === 'string') return t('событие ожидания: {f}', { f: filtersText([{ property: o.waited_event, op: String(o.op), value: o.value }]) });
  if (typeof o.context === 'string') return t('контекст: {f}', { f: filtersText([{ property: o.context, op: String(o.op), value: o.value }]) });
  return JSON.stringify(c).slice(0, 80);
}

export function triggerText(tg: Trigger): string {
  switch (tg.type) {
    case 'event':
      return t('Событие {e}{w}', { e: tg.event, w: tg.where?.length ? ` (${filtersText(tg.where)})` : '' });
    case 'segment_entry':
      return t('Вход в сегмент {s}', { s: tg.segment });
    case 'date':
      return t('{a}{o} в {at}{y}', { a: tg.attribute, o: tg.offset_days ? ` ${tg.offset_days > 0 ? '+' : '−'}${Math.abs(tg.offset_days)} ${t('дн.')}` : '', at: tg.at_local, y: tg.recurrence === 'yearly' ? t(', ежегодно') : '' });
    case 'api':
      return 'POST /v1/journeys/:key/enrollments';
  }
}

/** One line under the step title on the canvas. */
export function stepSummary(s: Step): string {
  switch (s.type) {
    case 'wait':
      return s.until_local ? t('до {time}{days} по времени контакта', { time: s.until_local.time, days: s.until_local.weekdays?.length ? ` (${s.until_local.weekdays.map((d) => t(WEEKDAYS[d])).join(', ')})` : '' }) : durationText(s.duration);
    case 'wait_for_event':
      return t('{e} · не дольше {d}', { e: s.event, d: durationText(s.timeout) });
    case 'branch':
      return t('{n} {w} + иначе', { n: s.conditions.length, w: t(plural(s.conditions.length, 'условие', 'условия', 'условий')) });
    case 'split':
      return s.variants.map((v) => `${v.id} ${v.weight}%`).join(' · ');
    case 'send_email':
      return `${s.template.alias ?? s.template.id ?? '—'}${typeof s.template.version === 'number' ? ` v${s.template.version}` : ` ${t('(последняя)')}`}${s.stream === 'transactional' ? ` ${t('· сервисное')}` : ''}`;
    case 'send_notification':
      return `${s.policy.key || '—'} · ${s.template.key || t('шаблон не выбран')}${s.stream === 'transactional' ? ` ${t('· сервисное')}` : ''}`;
    case 'webhook':
      return s.endpoint_id || t('эндпоинт не выбран');
    case 'update_contact':
      return [...Object.keys(s.set ?? {}), ...(s.unset ?? []).map((k) => `−${k}`)].join(', ') || t('ничего');
    case 'digest':
      return t('{e} за {d}, до {n}', { e: s.event, d: durationText(s.window), n: s.max_items });
    case 'exit':
      return s.reason;
  }
}

/** Outgoing edges with their labels in words. */
export function edgesOf(s: Step): { label: string; to: string | null; key: string }[] {
  switch (s.type) {
    case 'wait':
    case 'send_email':
    case 'send_notification':
    case 'webhook':
    case 'update_contact':
    case 'digest':
      return [{ label: '', to: s.next, key: 'next' }];
    case 'wait_for_event':
      return [
        { label: t('событие пришло'), to: s.on_event, key: 'on_event' },
        { label: t('тайм-аут'), to: s.on_timeout, key: 'on_timeout' },
      ];
    case 'branch':
      return [...s.conditions.map((c, i) => ({ label: conditionText(c.when).slice(0, 48), to: c.next, key: `conditions.${i}` })), { label: t('иначе'), to: s.else, key: 'else' }];
    case 'split':
      return s.variants.map((v, i) => ({ label: `${v.id} · ${v.weight}%`, to: v.next, key: `variants.${i}` }));
    case 'exit':
      return [];
  }
}

// ---------- layout ----------

export interface LaidNode {
  id: string;
  x: number;
  y: number;
  level: number;
  reachable: boolean;
}

export const NODE_W = 232;
export const NODE_H = 118;
const COL = 268;
const ROW = 178;

/**
 * Top-down layout by DAG levels (longest path from the start, so a step sits below every step leading to it);
 * siblings keep the order of the edges. The graph's own `layout` wins when it places every step.
 */
export function layoutGraph(g: Graph): { nodes: LaidNode[]; width: number; height: number; triggerX: number } {
  const ids = Object.keys(g.steps);
  const level = new Map<string, number>();
  const order: string[] = [];
  const visit = (id: string | null, depth: number, path: Set<string>) => {
    if (!id || !g.steps[id] || path.has(id)) return;
    if ((level.get(id) ?? -1) >= depth) return;
    if (!level.has(id)) order.push(id);
    level.set(id, depth);
    path.add(id);
    for (const e of edgesOf(g.steps[id])) visit(e.to, depth + 1, path);
    path.delete(id);
  };
  visit(g.start, 1, new Set());
  const reachable = new Set(level.keys());
  let maxLevel = Math.max(1, ...level.values());
  for (const id of ids) {
    if (!level.has(id)) {
      maxLevel += 1;
      level.set(id, maxLevel);
      order.push(id);
    }
  }
  const custom = g.layout && ids.every((id) => g.layout?.[id]);
  const rows = new Map<number, string[]>();
  for (const id of order) {
    const l = level.get(id)!;
    rows.set(l, [...(rows.get(l) ?? []), id]);
  }
  const widest = Math.max(1, ...[...rows.values()].map((r) => r.length));
  const width = Math.max(widest * COL, COL);
  const nodes: LaidNode[] = [];
  for (const [l, list] of rows) {
    const rowW = list.length * COL;
    list.forEach((id, i) => {
      const pos = custom ? g.layout![id] : { x: (width - rowW) / 2 + i * COL + (COL - NODE_W) / 2, y: l * ROW };
      nodes.push({ id, x: pos.x, y: pos.y, level: l, reachable: reachable.has(id) });
    });
  }
  const height = Math.max(...nodes.map((n) => n.y + NODE_H), ROW) + 70;
  const realWidth = Math.max(width, ...nodes.map((n) => n.x + NODE_W + 18));
  return { nodes, width: realWidth, height, triggerX: realWidth / 2 - NODE_W / 2 };
}

/** Steps in reading order (the phone view and the «Маяк» list): by level, then by edge order. */
export function stepsInOrder(g: Graph): string[] {
  return layoutGraph(g)
    .nodes.slice()
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((n) => n.id);
}

// ---------- editing helpers ----------

export const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** A fresh id like `send_email_2` that is not taken yet. */
export function freshId(g: Graph, type: StepType): string {
  const base = type === 'send_email' ? 'email' : type === 'send_notification' ? 'notify' : type === 'wait_for_event' ? 'wait_event' : type;
  for (let i = 1; i < 1000; i++) {
    const id = `${base}_${i}`;
    if (!g.steps[id]) return id;
  }
  return `${base}_${Date.now()}`;
}

export function newStep(type: StepType, next: string | null, from?: string): Step {
  switch (type) {
    case 'wait':
      return { type, duration: { days: 1 }, next };
    case 'wait_for_event':
      return { type, event: 'order.paid', timeout: { days: 1 }, on_event: next, on_timeout: next };
    case 'branch':
      return { type, conditions: [{ when: { event: 'order.paid', within: { days: 7 } }, next }], else: next };
    case 'split':
      return { type, variants: [{ id: 'a', weight: 50, next }, { id: 'b', weight: 50, next }] };
    case 'send_email':
      return { type, template: { alias: '', version: 'latest' }, variables: {}, ...(from ? {} : {}), next };
    case 'send_notification':
      return { type, template: { key: '', version: 'latest' }, policy: { key: '', version: 'latest' }, variables: {}, next };
    case 'webhook':
      return { type, endpoint_id: '', include_attributes: [], next };
    case 'update_contact':
      return { type, set: {}, unset: [], next };
    case 'digest':
      return { type, event: 'comment.created', window: { hours: 1 }, max_items: 10, item_template: '{{title}}', next };
    case 'exit':
      return { type, reason: 'done' };
  }
}

/** Steps a validation path points at: `steps.remind_1.template` → remind_1. */
export function stepOfPath(path: string): string | null {
  const m = /^steps\.([a-z0-9_]{1,40})(?:\.|$)/.exec(path) ?? /^steps\[["']?([a-z0-9_]{1,40})/.exec(path);
  return m ? m[1] : null;
}

/** Sample values for a template preview: every variable shows its own name, nested paths included. */
export function sampleVariables(names: string[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const name of names) {
    const parts = name.split('.');
    let cur = out;
    parts.forEach((p, i) => {
      if (i === parts.length - 1) cur[p] = name === 'unsubscribe_url' ? '#unsubscribe' : `[${name}]`;
      else cur = (cur[p] = (cur[p] as Record<string, unknown>) ?? {}) as Record<string, unknown>;
    });
  }
  return out;
}

export const shortHash = (h: string) => h.slice(0, 8);
export const pct = (v: number, digits = 1) => `${(v * 100).toLocaleString(currentLocale(), { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false })} %`;
export const pp = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toLocaleString(currentLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false })} ${t('п.п.')}`;
